/**
 * Tracks when the user last interacted, so a request can report how long ago the
 * interaction happened rather than when it happened. A browser wall clock can be
 * minutes out and would poison a server-side duration; an elapsed value measured
 * against `performance.now()` is monotonic and cannot be.
 */

const INTERACTION_EVENTS = ["pointerdown", "keydown"] as const;

/**
 * Past this gap the last interaction is no longer plausibly the cause of the
 * request — a poll or a timer-driven mutation rather than a click — so we report
 * nothing and let the server fall back to its own ingress time.
 */
const STALE_INTERACTION_MS = 30_000;

let lastInteractionAt: number | null = null;
let isListening = false;

export function startInteractionClock(): void {
  if (isListening || typeof window === "undefined") return;
  isListening = true;

  for (const eventName of INTERACTION_EVENTS) {
    window.addEventListener(
      eventName,
      () => {
        lastInteractionAt = performance.now();
      },
      // Capture, so a handler that stops propagation cannot hide the interaction.
      { capture: true, passive: true },
    );
  }
}

/** Milliseconds since the last interaction, or null when there is nothing trustworthy to report. */
export function getElapsedSinceInteractionMs(): number | null {
  if (lastInteractionAt === null) return null;

  const elapsedMs = Math.round(performance.now() - lastInteractionAt);
  if (elapsedMs < 0 || elapsedMs > STALE_INTERACTION_MS) return null;
  return elapsedMs;
}
